import React, { useState, useEffect, useContext } from "react";
import { mainContext } from "@ts/constants";
import { Modal } from "antd";
import moment from "moment";
import {
    updateIsSessionExpired,
    updateWarningsModal,
} from "@features/modalSlice";
import { useDispatch, useSelector } from "react-redux";
import { doLogout } from "@services/UserService";
import { IMainContext } from "@ts/common-types";
import { TbClockHour9 } from "react-icons/tb";
import { FaExclamationCircle } from "react-icons/fa";
import { FaRotateRight } from "react-icons/fa6";
import { sendRequest } from "@/ts/retrieve-data";
import store from "@/store";

type TBoolean = { value: boolean };

type TDisplay = {
    warningsModalDisp?: TBoolean;
    isSessionExpired?: TBoolean;
};

type TWarningsModal = {
    expiryTime: number;
};

const WarningsModal: React.FC<TWarningsModal> = React.memo(({ expiryTime }) => {
    const dispatch = useDispatch();
    const show = useSelector(
        (state: TDisplay) => state.warningsModalDisp.value
    );
    const isSessionExpired = useSelector(
        (state: TDisplay) => state.isSessionExpired.value
    );
    const { sessionExpiredAlert } = useContext<IMainContext>(mainContext);
    const handleClose = () => {
        dispatch(updateWarningsModal(false));
    };

    const [timer, setTimer] = useState<string>();

    const fetchNewToken = async () => {
        try {
            const response = await sendRequest({
                type: `auth/refresh`,
                method: "get",
            });

            if (!response.status) {
                throw new Error(response.message);
            }

            return response.status;
        } catch (err) {
            store.dispatch(updateWarningsModal(true));
            store.dispatch(updateIsSessionExpired(true));
            await doLogout();
        }
    };

    useEffect(() => {
        const counterFunction = async () => {
            if (expiryTime === 0) {
                await doLogout();
            } else {
                setTimer(moment.utc(expiryTime * 1000).format("mm:ss"));
            }
        };
        counterFunction();
    }, [expiryTime]);

    const SessionExpiryComponent = () => {
        return (
            <div>
                <div style={{ textAlign: "center" }}>
                    <TbClockHour9
                        className="text-secondary"
                        style={{ fontSize: "1.2rem" }}
                    />{" "}
                    Your session will expire in
                    <div style={{ fontSize: "2rem" }}>{timer}</div>
                    {sessionExpiredAlert ? (
                        <div style={{ textAlign: "center" }}>
                            <div>
                                It's been 24 hours since you last "Logged In",
                                This session is about to end.
                            </div>
                            <button
                                className="btn btn-primary my-2"
                                onClick={async () => await doLogout()}
                            >
                                Log In
                            </button>
                        </div>
                    ) : (
                        <div>
                            <div>
                                {" "}
                                Please click "Continue" to keep working; or
                                click "Log Off" to end your session.
                            </div>
                            <div className="mt-2">
                                <button
                                    className="btn btn-success m-3"
                                    onClick={() => {
                                        fetchNewToken()
                                            .then(async (response) => {
                                                if (response) {
                                                    handleClose();
                                                } else {
                                                    await doLogout();
                                                }
                                            })
                                            .catch((err) => {
                                                console.error(err);
                                                dispatch(
                                                    updateWarningsModal(true)
                                                );
                                                dispatch(
                                                    updateIsSessionExpired(
                                                        false
                                                    )
                                                );
                                            });
                                    }}
                                >
                                    Continue
                                </button>
                                <button
                                    className="btn btn-warning"
                                    onClick={async () => await doLogout()}
                                >
                                    Log Off
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const FetchTokenErrorComponent = () => {
        return (
            <div style={{ textAlign: "center" }}>
                <FaExclamationCircle
                    className="text-warning"
                    style={{ fontSize: "3rem", marginRight: "10px" }}
                />
                <div style={{ fontSize: "1rem" }}>
                    <div>
                        <span>Oops,looks like something went wrong. </span>
                    </div>
                    <div>
                        <div
                            className="anchor-class"
                            onClick={() => window.location.reload()}
                        >
                            <FaRotateRight /> Click to reload
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal
                title={
                    <div
                        className="d-flex pb-2 align-items-center"
                        style={{ fontSize: "1.2rem" }}
                    >
                        {isSessionExpired ? "Session Timeout" : "Error Warning"}
                    </div>
                }
                open={show}
                keyboard={false}
                maskClosable={false}
                footer={null}
                styles={{ body: { marginBottom: "16px" } }}
                closable={false}
            >
                {isSessionExpired ? (
                    <SessionExpiryComponent />
                ) : (
                    <FetchTokenErrorComponent />
                )}
            </Modal>
        </>
    );
});

export default WarningsModal;
